import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, isCoach } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1604480133435-25b86862d276?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Login() {
  const { user, requestOtp, verifyOtp } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [invalidCode, setInvalidCode] = useState(false);
  const verifyLock = useRef(false);

  const destination = (account) => {
    if (isCoach(account)) return "/coach";
    return account.status === "active" && account.plan_id ? "/app" : "/app/billing";
  };

  useEffect(() => {
    if (step !== 2 || cooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setCooldown((remaining) => remaining - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [step, cooldown]);

  if (user) return <Navigate to={destination(user)} replace />;

  const sendCode = async (event) => {
    event?.preventDefault();
    const trimmedEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await requestOtp(trimmedEmail);
      setEmail(trimmedEmail);
      setCode("");
      setStep(2);
      setCooldown(60);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (submittedCode = code) => {
    if (submittedCode.length !== 6 || verifyLock.current) return;
    verifyLock.current = true;
    setBusy(true);
    setError("");
    try {
      const account = await verifyOtp(email.trim(), submittedCode);
      nav(destination(account));
    } catch (err) {
      setInvalidCode(err?.response?.status === 401);
      setCode("");
      setError(err?.response?.status === 401 ? "Invalid or expired code. Please try again." : errMsg(err));
    } finally {
      verifyLock.current = false;
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setError("");
    try {
      await requestOtp(email.trim());
      setCooldown(60);
      setCode("");
      setInvalidCode(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const changeEmail = () => {
    setStep(1);
    setCode("");
    setError("");
    setInvalidCode(false);
    setCooldown(0);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] relative z-10">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Athlete training" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17]/40 to-[#0B0F17]" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <div className="eyebrow text-[#FF3B30]">Coach operating system</div>
          <h1 className="text-6xl font-extrabold uppercase leading-[0.95] mt-3 max-w-lg">Programs built in minutes. Reviewed by you.</h1>
          <p className="text-slate-400 mt-4 max-w-md">Plans, check-ins, progress and payments in one command center, with AI drafts that never reach a client without your approval.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={step === 1 ? sendCode : (event) => { event.preventDefault(); verifyCode(); }} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-[#FF3B30] grid place-items-center rounded-sm font-display font-extrabold text-xl">K</div>
            <span className="font-display text-2xl font-bold tracking-wide">K-FITHUB</span>
          </div>
          <h2 className="text-4xl font-bold uppercase">{step === 1 ? "Sign in" : "Check your email"}</h2>
          <p className="text-sm text-slate-400 mt-1 mb-8">{step === 1 ? "Enter your email to receive a secure sign-in code." : `Enter the 6-digit code sent to ${email}.`}</p>
          <div className="space-y-4">
            <div key={step} className="animate-in fade-in slide-in-from-right-2 duration-200">
              {step === 1 ? (
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)}
                    className="mt-1.5 h-11 border-[#263244] bg-[#111827]" data-testid="login-email-input" />
                </div>
              ) : (
                <div>
                  <Label htmlFor="email-code">6-digit code</Label>
                  <InputOTP id="email-code" maxLength={6} value={code} onChange={(value) => { setCode(value); setInvalidCode(false); if (error) setError(""); }} onComplete={verifyCode} disabled={busy} autoFocus containerClassName="mt-2" data-testid="login-otp-input">
                    <InputOTPGroup className="w-full justify-between gap-2">
                      {Array.from({ length: 6 }, (_, index) => <InputOTPSlot key={index} index={index} className={`h-12 w-11 flex-1 rounded-md border-x border-[#263244] bg-[#111827] text-base ${invalidCode ? "border-red-500 text-red-300" : ""}`} />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              )}
            </div>
            {error && <div className="text-sm text-red-400" role="alert" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy || (step === 2 && code.length !== 6)} className="w-full h-11 uppercase tracking-wider font-semibold" data-testid="login-submit-btn">
              {busy ? (step === 1 ? "Sending code…" : "Verifying…") : <>{step === 1 ? "Continue" : "Verify & Sign In"} <ArrowRight size={16} className="ml-1" /></>}
            </Button>
            {step === 2 && <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={changeEmail} disabled={busy} className="text-slate-400 transition-colors hover:text-white">Change email</button>
              <button type="button" onClick={resendCode} disabled={busy || cooldown > 0} className="text-slate-400 transition-colors hover:text-[#FF3B30] disabled:cursor-not-allowed disabled:opacity-50">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </div>}
          </div>
          <p className="text-xs text-slate-500 mt-8">{step === 1 ? "Passwordless sign-in for K-FitHUB clients and team members." : "The code expires after a short time. Check your spam folder if it doesn’t arrive."}</p>
        </form>
      </div>
    </div>
  );
}
